import { Controller } from "@hotwired/stimulus"

const DRAG_EVENTS = [
  ["mousemove", "mouseup"],
  ["touchmove", "touchend"]
]

export default class extends Controller {
  static targets = ["panel"]

  static values = {
    storageKey: String,
    direction: { type: String, default: "right" },
    minWidthPercent: { type: Number, default: 20 },
    maxWidthPercent: { type: Number, default: 60 }
  }

  connect() {
    this.isDragging = false
    this.boundMouseMove = this.#onMouseMove.bind(this)
    this.boundMouseUp = this.#onMouseUp.bind(this)
    this.#restoreWidth()
  }

  disconnect() {
    if (this.rafId) cancelAnimationFrame(this.rafId)
    if (this.isDragging) {
      document.body.style.cursor = ""
      document.body.style.userSelect = ""
    }
    this.#toggleDragListeners("remove")
  }

  startDrag(event) {
    event.preventDefault()
    this.isDragging = true
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"
    this.#toggleDragListeners("add")
  }

  #onMouseMove(event) {
    if (!this.isDragging) return

    const clientX = event.touches ? event.touches[0].clientX : event.clientX
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.rafId = requestAnimationFrame(() => {
      const containerRect = this.element.getBoundingClientRect()
      const maxWidth = containerRect.width * (this.maxWidthPercentValue / 100)

      let newWidth = this.directionValue === "left"
        ? clientX - containerRect.left
        : containerRect.right - clientX
      const minWidth = containerRect.width * (this.minWidthPercentValue / 100)
      newWidth = Math.max(newWidth, minWidth)
      newWidth = Math.min(newWidth, maxWidth)

      this.panelTarget.style.flexBasis = `${newWidth}px`
    })
  }

  #onMouseUp() {
    if (!this.isDragging) return

    this.isDragging = false
    document.body.style.cursor = ""
    document.body.style.userSelect = ""
    this.#toggleDragListeners("remove")

    const width = this.panelTarget.style.flexBasis
    if (width) {
      localStorage.setItem(this.storageKeyValue, width)
    }
  }

  #toggleDragListeners(action) {
    for (const [moveEvent, endEvent] of DRAG_EVENTS) {
      document[`${action}EventListener`](moveEvent, this.boundMouseMove)
      document[`${action}EventListener`](endEvent, this.boundMouseUp)
    }
  }

  #restoreWidth() {
    const savedWidth = localStorage.getItem(this.storageKeyValue)
    if (savedWidth && this.hasPanelTarget) {
      const containerWidth = this.element.getBoundingClientRect().width
      const minWidth = containerWidth * (this.minWidthPercentValue / 100)
      const maxWidth = containerWidth * (this.maxWidthPercentValue / 100)
      const parsed = parseFloat(savedWidth)
      const clamped = Math.max(minWidth, Math.min(maxWidth, parsed))
      this.panelTarget.style.flexBasis = `${clamped}px`
    }
  }
}
